/**
 * iter-370 → iter-371 — ProjectDocsPanel with Visibility Toggles
 *
 * Compact drop-in panel that replicates the Project Dashboard's Documents
 * tab. Same GET/POST/DELETE endpoints (/projects/:id/documents) plus PUT
 * /documents/:id for edits. Now supports per-document visibility toggles
 * (`visible_to_client`, `visible_to_vendor`) — matches the existing Edit
 * Document dialog UX so the same permissions apply from either edit
 * surface.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, FileSpreadsheet, Image as ImageIcon, FolderOpen,
  Trash2, ExternalLink, Plus, Upload, Link as LinkIcon,
  Edit2, User, Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { value: 'drawing', label: 'Drawing / Plan' },
  { value: 'image', label: 'Site Photo' },
  { value: '3d_render', label: '3D Render' },
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'specification', label: 'Specification' },
  { value: 'other', label: 'Other' },
];

const emptyForm = {
  name: '',
  url: '',
  doc_type: 'drawing',
  visible_to_client: false,
  visible_to_vendor: false,
};

function VisibilityBadges({ doc }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {doc.visible_to_client && (
        <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] px-1.5 py-0" title="Visible in the Client-shared Public BOQ link">
          <User size={10} className="mr-0.5" /> Client
        </Badge>
      )}
      {doc.visible_to_vendor && (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0" title="Visible in the Vendor-shared Public BOQ link">
          <Building2 size={10} className="mr-0.5" /> Vendor
        </Badge>
      )}
      {!doc.visible_to_client && !doc.visible_to_vendor && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-stone-500">Internal only</Badge>
      )}
    </div>
  );
}

function DocFormFields({ form, setForm, uploadType, setUploadType, isEdit = false }) {
  return (
    <div className="space-y-3">
      {!isEdit && (
        <div className="grid grid-cols-4 gap-2">
          <Button type="button" variant={uploadType === 'link' ? 'default' : 'outline'} onClick={() => setUploadType('link')} className="flex flex-col items-center p-3 h-auto">
            <LinkIcon size={18} /><span className="text-xs mt-1">URL</span>
          </Button>
          <Button type="button" variant={uploadType === 'gdrive' ? 'default' : 'outline'} onClick={() => setUploadType('gdrive')} className="flex flex-col items-center p-3 h-auto">
            <FolderOpen size={18} /><span className="text-xs mt-1">G-Drive</span>
          </Button>
          <Button type="button" variant={uploadType === 'onedrive' ? 'default' : 'outline'} onClick={() => setUploadType('onedrive')} className="flex flex-col items-center p-3 h-auto">
            <FolderOpen size={18} /><span className="text-xs mt-1">OneDrive</span>
          </Button>
          <Button type="button" variant="outline" disabled className="flex flex-col items-center p-3 h-auto">
            <Upload size={18} /><span className="text-xs mt-1">Upload</span>
          </Button>
        </div>
      )}
      <div>
        <Label className="text-sm">Document Name *</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Floor Plan - Ground Floor" data-testid="boq-docs-name-input" />
      </div>
      <div>
        <Label className="text-sm">
          {uploadType === 'gdrive' ? 'Google Drive Link *' : uploadType === 'onedrive' ? 'OneDrive Link *' : 'Document URL *'}
        </Label>
        <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="boq-docs-url-input" />
      </div>
      <div>
        <Label className="text-sm">Document Type</Label>
        <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
          <SelectTrigger data-testid="boq-docs-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* Visibility toggles — parity with Edit Document dialog */}
      <div className="bg-stone-50 border border-stone-200 rounded-md p-3">
        <p className="text-sm font-medium text-stone-700 mb-2">Visible to:</p>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${isEdit ? 'edit_' : 'new_'}visible_to_client`}
              checked={!!form.visible_to_client}
              onCheckedChange={(v) => setForm({ ...form, visible_to_client: !!v })}
              data-testid={`${isEdit ? 'edit-' : 'new-'}docs-visible-client`}
            />
            <Label htmlFor={`${isEdit ? 'edit_' : 'new_'}visible_to_client`} className="flex items-center gap-1 text-sm cursor-pointer">
              <User size={14} className="text-purple-600" /> Client
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${isEdit ? 'edit_' : 'new_'}visible_to_vendor`}
              checked={!!form.visible_to_vendor}
              onCheckedChange={(v) => setForm({ ...form, visible_to_vendor: !!v })}
              data-testid={`${isEdit ? 'edit-' : 'new-'}docs-visible-vendor`}
            />
            <Label htmlFor={`${isEdit ? 'edit_' : 'new_'}visible_to_vendor`} className="flex items-center gap-1 text-sm cursor-pointer">
              <Building2 size={14} className="text-amber-700" /> Vendor
            </Label>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-1">Only selected roles can see this document in their published BOQ view.</p>
      </div>
    </div>
  );
}

export default function ProjectDocsPanel({ projectId, isAdmin = true }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('link');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDocs = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/documents`, { headers });
      setDocs(r.data || []);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchDocs(); }, [projectId]);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.url.trim()) { toast.error('Name and URL are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/documents`, {
        name: form.name.trim(),
        url: form.url.trim(),
        doc_type: form.doc_type,
        source: uploadType,
        visible_to_client: !!form.visible_to_client,
        visible_to_vendor: !!form.visible_to_vendor,
      }, { headers });
      toast.success('Document added');
      setForm(emptyForm);
      setUploadType('link');
      setDialogOpen(false);
      fetchDocs();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to add document');
    } finally { setSaving(false); }
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      name: doc.name || '',
      url: doc.url || '',
      doc_type: doc.doc_type || 'drawing',
      visible_to_client: !!doc.visible_to_client,
      visible_to_vendor: !!doc.visible_to_vendor,
    });
  };

  const handleEditSave = async (e) => {
    e?.preventDefault?.();
    if (!editingDoc) return;
    if (!editForm.name.trim() || !editForm.url.trim()) { toast.error('Name and URL are required'); return; }
    setEditSaving(true);
    try {
      await axios.put(`${API}/documents/${editingDoc.id}`, {
        name: editForm.name.trim(),
        url: editForm.url.trim(),
        doc_type: editForm.doc_type,
        source: editingDoc.source || 'link',
        visible_to_client: !!editForm.visible_to_client,
        visible_to_vendor: !!editForm.visible_to_vendor,
      }, { headers });
      toast.success('Document updated');
      setEditingDoc(null);
      fetchDocs();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to update document');
    } finally { setEditSaving(false); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${docId}`, { headers });
      toast.success('Deleted');
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const docIcon = (t) => {
    if (t === 'image' || t === '3d_render') return <ImageIcon size={18} className="text-purple-600" />;
    if (t === 'drawing') return <FileSpreadsheet size={18} className="text-blue-600" />;
    return <FileText size={18} className="text-stone-600" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-stone-200 mt-4 p-4" data-testid="boq-edit-docs-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-stone-900 flex items-center gap-2">
            <FolderOpen size={18} className="text-amber-700" />
            Client & Vendor Documents
          </h3>
          <p className="text-xs text-stone-500">Drawings, site photos, contracts and specifications. Toggle visibility per role.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="boq-docs-add-btn">
              <Plus size={14} className="mr-1" /> Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Project Document</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd}>
              <DocFormFields form={form} setForm={setForm} uploadType={uploadType} setUploadType={setUploadType} />
              <div className="flex justify-end gap-2 pt-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="boq-docs-save-btn">
                  {saving ? 'Adding…' : 'Add Document'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-stone-400">Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="py-6 text-center bg-stone-50/60 border border-dashed border-stone-200 rounded-md">
          <FolderOpen size={32} className="mx-auto text-stone-300 mb-2" />
          <p className="text-sm text-stone-500">No documents linked yet.</p>
          <p className="text-xs text-stone-400">Add drawings, site photos, and contracts for this project.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => (
            <div key={d.id} className="border border-stone-200 rounded-md p-3 hover:shadow-sm transition-shadow bg-white" data-testid={`boq-doc-card-${d.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {docIcon(d.doc_type)}
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {d.source === 'gdrive' ? 'G-Drive' : d.source === 'onedrive' ? 'OneDrive' : 'Link'}
                  </Badge>
                </div>
                {isAdmin && (
                  <div className="inline-flex items-center gap-0.5">
                    <button onClick={() => openEdit(d)} className="p-1 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`boq-doc-edit-${d.id}`} title="Edit"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(d.id)} className="p-1 text-red-400 hover:text-red-600 rounded" data-testid={`boq-doc-delete-${d.id}`} title="Delete"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              <h4 className="font-medium text-stone-900 text-sm line-clamp-1" title={d.name}>{d.name}</h4>
              <p className="text-[11px] text-stone-500 capitalize mb-1">{(d.doc_type || 'other').replace('_', ' ')}</p>
              <VisibilityBadges doc={d} />
              <a href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2">
                <ExternalLink size={12} /> Open
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Edit Document Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(v) => { if (!v) setEditingDoc(null); }}>
        <DialogContent className="max-w-lg" data-testid="boq-docs-edit-dialog">
          <DialogHeader><DialogTitle>Edit Document</DialogTitle></DialogHeader>
          {editingDoc && (
            <form onSubmit={handleEditSave}>
              <DocFormFields form={editForm} setForm={setEditForm} uploadType={editingDoc.source || 'link'} setUploadType={() => { }} isEdit />
              <div className="flex justify-end gap-2 pt-3">
                <Button type="button" variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
                <Button type="submit" disabled={editSaving} data-testid="boq-docs-edit-save-btn">
                  {editSaving ? 'Updating…' : 'Update Document'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
